import React, { Suspense, lazy, useEffect, useState } from 'react';
import './App.css';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { useInView as useInViewHook } from 'react-intersection-observer';


// Eagerly loaded — lightweight, on the critical path
import HomePage from './pages/HomePage';
import { AdminRoute } from './components/admin/AdminRoute';
import CustomCursor from './components/CustomCursor';
import CookieConsent from './components/CookieConsent';
import NewsletterPopup from './components/NewsletterPopup';

// Lazily loaded — split into separate chunks to reduce initial bundle
const AboutPage = lazy(() => import('./pages/AboutPage'));
const VideosPage = lazy(() => import('./pages/VideosPage'));
const ForbesPage = lazy(() => import('./pages/ForbesPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const PresetsPage = lazy(() => import('./pages/PresetsPage'));
const ClientsPage = lazy(() => import('./pages/ClientsPage'));
const GalleryPage = lazy(() => import('./pages/GalleryPage'));
const ServicesPage = lazy(() => import('./pages/ServicesPage'));
const PortfolioPage = lazy(() => import('./pages/PortfolioPage'));
const CareersPage = lazy(() => import('./pages/CareersPage'));
const AdminAnalytics = lazy(() => import('./pages/AdminAnalytics'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const AdminLogin = lazy(() => import('./pages/AdminLogin'));

// WebGL ambient layer
import { ScrollVelocityProvider } from './context/ScrollVelocityContext';
import WebGLBackground from './components/webgl/WebGLBackground';

const App = () => {
  return (
    <Router>
      <ScrollVelocityProvider>
        <div className="App">
          {/* WebGL ambient background — fixed, z-index: -1 */}
          <Suspense fallback={null}>
            <WebGLBackground />
          </Suspense>

          <VoidStudiosApp />
        </div>
      </ScrollVelocityProvider>
    </Router>
  );
};

const VoidStudiosApp = () => {
  const [isLoaded, setIsLoaded] = useState(false);

  const location = useLocation();
  const isAdminPath = location.pathname.startsWith('/admin');

  // GA4 Pageview tracking on route change
  useEffect(() => {
    if (window.gtag) {
      window.gtag('config', 'G-NX4BELWXHZ', {
        page_path: location.pathname,
      });
    }
  }, [location]);

  return (
    <div className="void-studios">
      {/* Custom Cursor - Global */}
      <CustomCursor />

      {/* Navigation Header - shown on all pages except admin */}
      {!isAdminPath && <Header />}

      {/* Global Cookie Consent Banner */}
      <CookieConsent />

      {/* Newsletter Popup — public pages only */}
      {!isAdminPath && <NewsletterPopup />}

      {/* Route-based content */}
      <Suspense fallback={null}>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/home" element={<HomePage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/videos" element={<VideosPage />} />
        <Route path="/forbes" element={<ForbesPage />} />
        <Route path="/presets" element={<PresetsPage />} />
        <Route path="/clients" element={<ClientsPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/gallery" element={<GalleryPage />} />
        <Route path="/portfolio" element={<PortfolioPage />} />
        <Route path="/services" element={<ServicesPage />} />
        <Route path="/careers" element={<CareersPage />} />

        {/* Admin Routes */}
        <Route path="/admin" element={<AdminLogin />} />
        <Route
          path="/admin/dashboard"
          element={
            <AdminRoute>
              <AdminDashboard />
            </AdminRoute>
          }
        />
      </Routes>
      </Suspense>
    </div>
  );
};

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [logoVisible, setLogoVisible] = useState(true);

  useEffect(() => {
    const handleWatermark = (e) => {
      // Only hide logo on desktop — 
      // on mobile logo is always visible
      const isMobile = window.innerWidth <= 768;
      if (isMobile) return;
      setLogoVisible(!e.detail.isVisible);
    };

    window.addEventListener('watermark-visibility', handleWatermark);
    return () => window.removeEventListener('watermark-visibility', handleWatermark);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  return (
    <>
      <motion.header
        className="fixed top-0 left-0 right-0 z-50 flex justify-between items-center p-6 md:p-5 bg-gradient-to-b from-black/20 to-transparent backdrop-blur-sm"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.2 }}
      >
        <motion.div
          className="cursor-pointer"
          whileHover={logoVisible ? { scale: 1.05 } : {}}
          onClick={logoVisible ? closeMenu : undefined}
          style={{
            opacity: (logoVisible && !isMenuOpen) ? 1 : 0,
            transition: 'opacity 300ms ease',
            pointerEvents: (logoVisible && !isMenuOpen) ? 'auto' : 'none'
          }}
        >
          <img
            src="/raw-visual-studio-logo.png"
            alt="RVS Logo"
            className="h-28 w-auto brightness-90"
          />
        </motion.div>
        <motion.div
          className="text-gray-200 text-sm font-medium tracking-widest cursor-pointer hover:text-white transition-all duration-300 bg-gray-800/30 backdrop-blur-sm px-4 py-2 rounded-full border border-gray-600/30 hover:border-gray-400/50"
          whileHover={{ scale: 1.05 }}
          onClick={toggleMenu}
          style={{
            minHeight: '44px',
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer'
          }}
        >
          BRING IT ON ✦✦✦
        </motion.div>
      </motion.header>

      {/* Navigation Menu Overlay */}
      <NavigationMenu isOpen={isMenuOpen} onClose={closeMenu} />
    </>
  );
};

const NavigationMenu = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const menuItems = [
    { name: 'HOME', path: '/home' },
    { name: 'SERVICES', path: '/services' },
    { name: 'GALLERY', path: '/gallery' },
    { name: 'CLIENTS', path: '/clients' },
    { name: 'ABOUT', path: '/about' },
    { name: 'CONTACT', path: '/contact' },
    { name: 'CAREERS', path: '/careers' }
  ];

  const handleNavigation = (path) => {
    navigate(path);
    onClose();
  };

  return (
    <motion.div
      className="fixed inset-0 z-[100] bg-gradient-to-b from-[#0a0a0a] to-[#000000]"
      style={{ pointerEvents: isOpen ? 'auto' : 'none', touchAction: 'manipulation' }}
      initial={{ opacity: 0 }}
      animate={{ opacity: isOpen ? 1 : 0 }}
      transition={{ duration: 0.4 }}
    >
      <button
        onClick={onClose}
        style={{
          position: 'absolute', top: '24px', right: '24px',
          zIndex: 110, color: 'white', fontSize: '24px',
          background: 'none', border: 'none', cursor: 'pointer',
          minWidth: '44px', minHeight: '44px',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          opacity: isOpen ? 1 : 0
        }}
      >
        ✕
      </button>

      <div
        onClick={() => handleNavigation('/home')}
        style={{
          position: 'absolute', top: '20px', left: '24px',
          cursor: 'pointer', opacity: isOpen ? 1 : 0
        }}
      >
        <img
          src="/raw-visual-studio-logo.png"
          alt="RVS Logo"
          style={{ height: '8rem', width: 'auto' }}
        />
      </div>

      <div
        style={{
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          minHeight: '100vh', gap: '0.25rem',
          opacity: isOpen ? 1 : 0
        }}
      >
        {menuItems.map((item) => (
          <div
            key={item.name}
            onClick={() => handleNavigation(item.path)}
            onTouchEnd={(e) => {
              e.preventDefault();
              handleNavigation(item.path);
            }}
            style={{
              cursor: 'pointer',
              WebkitTapHighlightColor: 'transparent',
              touchAction: 'manipulation',
              userSelect: 'none',
              marginBottom: '0.25rem'
            }}
          >
            <HoverSlideText
              text={item.name}
              onClick={() => handleNavigation(item.path)}
            />
          </div>
        ))}
      </div>
    </motion.div>
  );
};

const HoverSlideText = ({ text, onClick }) => {
  const [isHovered, setIsHovered] = useState(false);
  const touchTimeout = React.useRef(null);

  const handleTouchStart = () => {
    setIsHovered(true);
  };

  const handleTouchEnd = () => {
    if (touchTimeout.current) clearTimeout(touchTimeout.current);
    touchTimeout.current = setTimeout(() => {
      setIsHovered(false);
    }, 350);
  };

  return (
    <div
      className="relative overflow-hidden cursor-pointer"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onClick={onClick}
    >
      <motion.div
        className="text-[5rem] md:text-[6rem] font-black text-gray-300/90 leading-none tracking-tight"
        animate={{
          y: isHovered ? '-100%' : '0%',
        }}
        transition={{
          duration: 0.4,
          ease: [0.23, 1, 0.320, 1],
        }}
      >
        {text}
      </motion.div>
      <motion.div
        className="absolute top-0 left-0 text-[5rem] md:text-[6rem] font-black text-gray-100 leading-none tracking-tight"
        animate={{
          y: isHovered ? '0%' : '100%',
        }}
        transition={{
          duration: 0.4,
          ease: [0.23, 1, 0.320, 1],
        }}
      >
        {text}
      </motion.div>
    </div>
  );
};

export default App;
